/**
 * Cross-component video player state cache.
 *
 * Allows multiple `VideoPlayer` instances rendering the same video (by URL)
 * to share playback state — current time, playback rate, volume, muted,
 * user-paused — so the user can seamlessly transition between the
 * carousel/card video and the full-screen `MediaViewer` video without losing
 * their place or settings.
 *
 * The store is a simple module-level singleton with a tiny subscribe API so
 * components can react to external updates (e.g., an unmounted player
 * persisting its final state for a newly-mounted player).
 */

export interface VideoPlayerSnapshot {
  currentTime: number;
  playbackRate: number;
  volume: number;
  isMuted: boolean;
  /** Whether the user explicitly paused (so we should not autoplay). */
  userPaused: boolean;
}

type Listener = (snapshot: VideoPlayerSnapshot) => void;

const DEFAULT_SNAPSHOT: VideoPlayerSnapshot = {
  currentTime: 0,
  playbackRate: 1,
  volume: 0.8,
  isMuted: true,
  userPaused: false,
};

const store = new Map<string, VideoPlayerSnapshot>();
const listeners = new Map<string, Set<Listener>>();

/**
 * Normalize a video URL key by stripping signed-URL query parameters so the
 * same video keeps a stable identity even after a CloudFront signed-URL
 * refresh.
 */
const SIGNED_QUERY_PARAMS = ["Expires", "Policy", "Signature", "Key-Pair-Id"];

function normalizeKey(rawUrl: string): string {
  if (!rawUrl) return "";
  try {
    const parsed = new URL(rawUrl);
    SIGNED_QUERY_PARAMS.forEach((param) => parsed.searchParams.delete(param));
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    // Not a fully-qualified URL; strip query string manually.
    const queryIndex = rawUrl.indexOf("?");
    return queryIndex >= 0 ? rawUrl.slice(0, queryIndex) : rawUrl;
  }
}

/**
 * Read the current snapshot for a given video URL. Returns the default
 * snapshot when nothing is cached.
 */
function getSnapshot(rawUrl: string): VideoPlayerSnapshot {
  const key = normalizeKey(rawUrl);
  if (!key) return { ...DEFAULT_SNAPSHOT };
  return store.get(key) ?? { ...DEFAULT_SNAPSHOT };
}

/**
 * Merge a partial snapshot into the cached state for a given video URL and
 * notify all subscribers.
 */
function setSnapshot(
  rawUrl: string,
  snapshot: Partial<VideoPlayerSnapshot>,
): void {
  const key = normalizeKey(rawUrl);
  if (!key) return;
  const previous = store.get(key) ?? { ...DEFAULT_SNAPSHOT };
  const next: VideoPlayerSnapshot = { ...previous, ...snapshot };
  store.set(key, next);
  const subs = listeners.get(key);
  if (subs) {
    subs.forEach((listener) => listener(next));
  }
}

/**
 * Subscribe to snapshot updates for a given video URL. Returns an
 * unsubscribe function. Listeners are not invoked synchronously; they fire
 * only on subsequent `setSnapshot` calls.
 */
function subscribe(rawUrl: string, listener: Listener): () => void {
  const key = normalizeKey(rawUrl);
  if (!key) {
    return () => undefined;
  }
  let bucket = listeners.get(key);
  if (!bucket) {
    bucket = new Set();
    listeners.set(key, bucket);
  }
  bucket.add(listener);
  return () => {
    const current = listeners.get(key);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) {
      listeners.delete(key);
    }
  };
}

export const videoPlayerStateStore = {
  getSnapshot,
  setSnapshot,
  subscribe,
};
